/**
 * AiStudioDock
 *
 * The persistent, non-blocking progress surface for an AI Studio run. Once the
 * launcher hands off, this is all the operator sees — a small card pinned to the
 * bottom-right corner that they can collapse to a pill and ignore.
 *
 * The reason it is a dock and not a modal: generation takes minutes. Blocking the
 * form for that long is the whole problem. Here the operator keeps typing while
 * each task reports for itself, and finished results drop into the form behind it.
 *
 * What each row communicates, and why:
 *   - a real per-task progress bar and the CURRENT STAGE ("Transcribing…"), so a
 *     long job visibly moves instead of looking hung
 *   - what a finished task actually filled in, so the operator knows where to look
 *   - a per-task error + Retry, because the common failure is one task hitting the
 *     Gemini daily quota while the others succeeded — re-running everything would
 *     waste the quota those successes already spent
 *
 * Stateless apart from collapsed/expanded; all run state comes from `useAiStudio`.
 */

import React, { useEffect, useState } from "react";
import {
  IoCheckmarkCircle,
  IoWarning,
  IoChevronDown,
  IoClose,
} from "react-icons/io5";
import { HiSparkles } from "react-icons/hi2";
import style from "./aiStudio.module.css";
import { taskIcon } from "./taskIcons";
import { formatElapsed } from "../../services/ai/aiStudioTasks";
import { TASK_STATE } from "../../hooks/useAiStudio";

/**
 * Rotating reassurance copy. A static "please wait" reads as frozen after the
 * first minute; changing the line every few seconds is a cheap, honest signal
 * that the run is alive — none of these claim progress that isn't happening.
 */
const REASSURANCE = [
  "You can keep filling in the form — finished results drop in automatically.",
  "Long videos take longer to transcribe. Nothing is stuck.",
  "Each step saves as it finishes, so a slow one can't lose the others.",
  "Feel free to close this panel — it keeps running in the background.",
];

const AiStudioDock = ({
  tasks = [],
  counts,
  overallProgress = 0,
  elapsedMs = 0,
  isRunning,
  isReview,
  onCancel,
  onRetryTask,
  onDone,
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const [tipIndex, setTipIndex] = useState(0);

  useEffect(() => {
    if (!isRunning) return;
    const id = setInterval(
      () => setTipIndex((i) => (i + 1) % REASSURANCE.length),
      7000
    );
    return () => clearInterval(id);
  }, [isRunning]);

  if (tasks.length === 0) return null;

  const allFailed = counts?.total > 0 && counts.failed === counts.total;
  const someFailed = (counts?.failed || 0) > 0;

  // ── Collapsed pill ──
  if (collapsed) {
    return (
      <button
        type="button"
        className={`${style.dockPill} ${isReview ? style.dockPillDone : ""}`}
        onClick={() => setCollapsed(false)}
      >
        <span className={`${style.dockPillIcon} ${isRunning ? style.spin : ""}`}>
          {isReview ? (
            someFailed ? <IoWarning /> : <IoCheckmarkCircle />
          ) : (
            <HiSparkles />
          )}
        </span>
        <span className={style.dockPillText}>
          {isRunning
            ? `AI · ${counts.done}/${counts.total} · ${formatElapsed(elapsedMs)}`
            : someFailed
            ? `AI finished · ${counts.failed} needs attention`
            : "AI finished"}
        </span>
        <span className={style.dockPillBar}>
          <span
            className={style.dockPillBarFill}
            style={{ width: `${overallProgress}%` }}
          />
        </span>
      </button>
    );
  }

  return (
    <section
      className={style.dock}
      role="status"
      aria-live="polite"
      aria-label="AI generation progress"
    >
      <header className={style.dockHead}>
        <span className={`${style.dockHeadIcon} ${isRunning ? style.pulse : ""}`}>
          {isReview ? (
            someFailed ? <IoWarning /> : <IoCheckmarkCircle />
          ) : (
            <HiSparkles />
          )}
        </span>

        <div className={style.dockHeadText}>
          <p className={style.dockTitle}>
            {isRunning && "AI is working on your video"}
            {isReview && !someFailed && "All done"}
            {isReview && someFailed && allFailed && "AI couldn't finish"}
            {isReview && someFailed && !allFailed && "Finished with some gaps"}
          </p>
          <p className={style.dockSub}>
            {isRunning
              ? `${counts.done} of ${counts.total} complete · ${formatElapsed(elapsedMs)} elapsed`
              : someFailed
              ? `${counts.done} of ${counts.total} succeeded — review the rest below.`
              : `${counts.total} task${counts.total === 1 ? "" : "s"} complete in ${formatElapsed(elapsedMs)}. Review and Save.`}
          </p>
        </div>

        <div className={style.dockHeadBtns}>
          <button
            type="button"
            className={style.dockIconBtn}
            onClick={() => setCollapsed(true)}
            aria-label="Minimise"
            title="Minimise"
          >
            <IoChevronDown />
          </button>
          {isReview && (
            <button
              type="button"
              className={style.dockIconBtn}
              onClick={onDone}
              aria-label="Dismiss"
              title="Dismiss"
            >
              <IoClose />
            </button>
          )}
        </div>
      </header>

      {isRunning && (
        <div className={style.dockOverall}>
          <div className={style.dockOverallTrack}>
            <div
              className={style.dockOverallFill}
              style={{ width: `${overallProgress}%` }}
            />
          </div>
        </div>
      )}

      <ul className={style.dockList}>
        {tasks.map((task) => {
          const Icon = taskIcon(task.icon);
          const running = task.state === TASK_STATE.RUNNING;
          const done = task.state === TASK_STATE.DONE;
          const failed = task.state === TASK_STATE.FAILED;

          return (
            <li
              key={task.id}
              className={[
                style.dockRow,
                running ? style.dockRowRunning : "",
                done ? style.dockRowDone : "",
                failed ? style.dockRowFailed : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              <span className={style.dockRowIcon}>
                {done ? <IoCheckmarkCircle /> : failed ? <IoWarning /> : <Icon />}
              </span>

              <div className={style.dockRowBody}>
                <div className={style.dockRowTop}>
                  <span className={style.dockRowLabel}>{task.label}</span>
                  <span className={style.dockRowState}>
                    {task.state === TASK_STATE.QUEUED && "Waiting"}
                    {running && (task.stageLabel || "Working")}
                    {done && "Done"}
                    {failed && "Failed"}
                  </span>
                </div>

                {running && (
                  <div className={style.dockRowTrack}>
                    <div
                      className={style.dockRowFill}
                      style={{ width: `${Math.max(4, task.progress)}%` }}
                    />
                  </div>
                )}

                {done && task.fills?.length > 0 && (
                  <p className={style.dockRowFills}>Filled: {task.fills.join(" · ")}</p>
                )}

                {failed && (
                  <div className={style.dockRowError}>
                    <p className={style.dockRowErrorText}>{task.error}</p>
                    {onRetryTask && (
                      <button
                        type="button"
                        className={style.dockRetry}
                        onClick={() => onRetryTask(task.id)}
                      >
                        Retry
                      </button>
                    )}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      <footer className={style.dockFoot}>
        {isRunning ? (
          <>
            <p className={style.dockTip} key={tipIndex}>
              {REASSURANCE[tipIndex]}
            </p>
            <button type="button" className={style.dockCancel} onClick={onCancel}>
              Stop
            </button>
          </>
        ) : (
          <>
            <p className={style.dockTip}>
              Everything AI produced is editable — review it before you save.
            </p>
            <button type="button" className={style.btnPrimarySm} onClick={onDone}>
              Got it
            </button>
          </>
        )}
      </footer>
    </section>
  );
};

export default AiStudioDock;
